import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Celebration, CelebrationJob, CelebrationJobLocation } from '../../../core/models/event.model';
import { EventService } from '../../../core/services/event.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-job-step',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatCardModule, MatSelectModule, MatButtonModule, MatIconModule, FormsModule],
  templateUrl: './job-step.component.html',
  styleUrls: ['./job-step.component.scss']
})
export class JobStepComponent implements OnChanges {
  @Input() eventId: string | null = null;
  @Output() jobSelected = new EventEmitter<CelebrationJobLocation>();

  jobForm: FormGroup;
  celebration: Celebration | null = null;
  isLoading = false;
  error: string | null = null;
  jobFilter: string = '';
  selectedGroupUuids: string[] | null = null;

  constructor(private fb: FormBuilder, private eventService: EventService) {
    this.jobForm = this.fb.group({
      job: ['', Validators.required]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['eventId'] && this.eventId) {
      this.fetchCelebration(this.eventId);
    }
  }

  fetchCelebration(eventId: string) {
    this.isLoading = true;
    this.error = null;
    this.eventService.getCelebrationById(eventId).subscribe({
      next: (celebration: Celebration) => {
        this.celebration = celebration;
        this.isLoading = false;
      },
      error: () => {
        this.error = 'Erro ao carregar funções/postos do evento.';
        this.isLoading = false;
      }
    });
  }

  onJobGroupSelect(job: CelebrationJob) {
    this.selectedGroupUuids = job.locationList.map(loc => loc.locationUuid);
    this.jobForm.get('job')?.setValue(null); // Limpa seleção individual
  }

  shouldShowCard(job: CelebrationJob, jobLocation: CelebrationJobLocation): boolean {
    const selected = this.jobForm.get('job')?.value;
    if (!selected) return true;
    if (selected.startsWith && selected.startsWith('group-')) {
      // Seleção de grupo
      const groupId = selected.replace('group-', '');
      return job.uuid === groupId;
    }
    // Seleção individual
    return jobLocation.locationUuid === selected;
  }

  onJobSelected(locationUuid: string) {
    console.log('Job selected:', locationUuid, this.celebration?.jobList);
    this.selectedGroupUuids = null; // Limpa seleção de grupo ao selecionar individual
    this.jobForm.get('job')?.setValue(locationUuid);
    // Busca o objeto completo do jobLocation selecionado
    let selected: CelebrationJobLocation | null = null;
    if (this.celebration) {
      for (const job of this.celebration.jobList) {
        const found = job.locationList.find(loc => loc.locationUuid === locationUuid);
        if (found) {
          selected = found;
          break;
        }
      }
    }
    if (selected) {
      this.jobSelected.emit(selected);
    }
  }

  onJobCardSelect(locationUuid: string) {
    this.jobForm.get('job')?.setValue(locationUuid);
    this.onJobSelected(locationUuid);
  }

  onSubmit() {
    if (this.jobForm.valid) {
      this.onJobSelected(this.jobForm.value.job);
    }
  }

  filteredJobs() {
    if (!this.celebration) return [];
    if (!this.jobFilter.trim()) return this.celebration.jobList;
    const filter = this.jobFilter.trim().toLowerCase();
    return this.celebration.jobList.filter(job =>
      job.name.toLowerCase().includes(filter) ||
      job.locationList.some(loc => loc.name.toLowerCase().includes(filter))
    );
  }

  filteredLocations(job: CelebrationJob) {
    if (!this.jobFilter.trim()) return job.locationList;
    const filter = this.jobFilter.trim().toLowerCase();
    return job.locationList.filter(loc => loc.name.toLowerCase().includes(filter));
  }

  formatDuration(minutes: number | undefined): string {
    if (!minutes) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
  }

  isGroupSelected(job: CelebrationJob): boolean {
    if (!this.selectedGroupUuids) return false;
    return job.locationList.every(loc => this.selectedGroupUuids?.includes(loc.locationUuid));
  }

  getSortedJobs() {
    if (!this.celebration) return [];
    return [...this.celebration.jobList].sort((a, b) => {
      const aHalf = a.maxStaff / 2;
      const bHalf = b.maxStaff / 2;
      const aStatus = a.activeStaff === 0 ? 2 : (a.activeStaff > aHalf ? 0 : 1);
      const bStatus = b.activeStaff === 0 ? 2 : (b.activeStaff > bHalf ? 0 : 1);
      if (aStatus !== bStatus) return aStatus - bStatus;
      // desempate: mais ativos primeiro
      return b.activeStaff - a.activeStaff;
    });
  }

  getJobStatusColor(job: CelebrationJob): string {
    if (job.activeStaff === 0) return 'red';
    if (job.activeStaff > job.maxStaff / 2) return 'green';
    return 'orange';
  }
}
